import { useEffect, useState } from 'react'
import { checkPhoto, type PhotoCheckResult } from '../../../api/photoCheck'
import type { PhotoCheckKind } from '../../../photo-slots'

/**
 * Состояние проверки фото в плитке поля (18.10): пока сервер думает — «проверяем…», затем «лицо найдено» /
 * «ноги не в кадре». Ответ кэшируется по ссылке файла (и сбой тоже — как «не измерено»): смена стиля или перерисовка
 * плитки не шлёт запрос заново. Нет проверки у поля или фото ещё грузится — null (полосы в плитке нет). Сбой — тоже
 * null: это подсказка, а не проверка.
 */
export interface SlotCheckState {
	status: 'wait' | 'ok' | 'warn'
	text: string
	note?: string
}

const cache = new Map<string, PhotoCheckResult>()
const pending = new Map<string, Promise<PhotoCheckResult>>()
const keyOf = (check: PhotoCheckKind, url: string) => `${check}:${url}`

function run(url: string, check: PhotoCheckKind): Promise<PhotoCheckResult> {
	const key = keyOf(check, url)
	let p = pending.get(key)
	if (!p) {
		p = checkPhoto(url, check)
			.catch((): PhotoCheckResult => ({ measured: false }))
			.then((r) => {
				cache.set(key, r)
				pending.delete(key)
				return r
			})
		pending.set(key, p)
	}
	return p
}

function toState(r: PhotoCheckResult): SlotCheckState | null {
	if (!r.measured || !r.text) return null
	return { status: r.ok ? 'ok' : 'warn', text: r.text, note: r.note }
}

export function usePhotoSlotCheck(check: PhotoCheckKind | null, url: string | null | undefined): SlotCheckState | null {
	const key = check && url ? keyOf(check, url) : null
	// Ответ лежит в кэше модуля; состояние нужно лишь, чтобы перерисовать плитку, когда он придёт.
	const [, bump] = useState(0)
	useEffect(() => {
		if (!key || !check || !url || cache.has(key)) return
		let alive = true
		void run(url, check).then(() => {
			if (alive) bump((n) => n + 1)
		})
		return () => {
			alive = false
		}
	}, [key, check, url])
	if (!key) return null
	const hit = cache.get(key)
	return hit ? toState(hit) : { status: 'wait', text: 'проверяем…' }
}

/** Для тестов: забыть ответы. */
export function resetPhotoSlotCheckCache(): void {
	cache.clear()
	pending.clear()
}
